import { getCryptoConfig } from '@/api/login.js';
import { sm2, sm3, sm4 } from 'sm-crypto';

const { publicKey, privateKey } = sm2.generateKeyPairHex();
let algorithm = 'SM4';
let config = {
  key: '',
  iv: '',
  mode: '',
  padding: '',
  enabled: false,
};

/**
 * 字符串转十六进制
 * @param str
 */
const strToHex = str => {
  let hexString = '';
  for (let i = 0; i < str?.length; i++) {
    let hex = str.charCodeAt(i).toString(16);
    hexString += hex.length === 1 ? '0' + hex : hex;
  }
  return hexString;
};

const getSign = () => {
  const timestamp = new Date().getTime();
  const chars = 'ABCDEFGHIJKLMNOPQRSTUVWXYZabcdefghijklmnopqrstuvwxyz0123456789';
  let nonce = '';
  const length = 32;
  for (let i = 0; i < length; i++) {
    const randomIndex = Math.floor(Math.random() * chars.length);
    nonce += chars.charAt(randomIndex);
  }
  const digestStr = sm3([String(timestamp), nonce, publicKey].sort().join(''));
  const signature = sm2.doSignature(digestStr, privateKey, {
    der: true,
    hash: true,
    userId: '1234567812345678',
  });
  let pk = encryptDataBySM2(publicKey);
  return {
    timestamp,
    nonce,
    signature,
    publicKey: pk,
  };
};

// 获取加密配置
export const getCrypto = () => {
  getCryptoConfig(getSign()).then(res => {
    algorithm = res.algorithm;
    if (algorithm === 'sm2') {
      config = decryptDataBySM2(res.config);
      if (config) {
        config = JSON.parse(config || {});
      }
    }
  });
};

// 加密
export const encrypt = word => {
  if (config.enabled) {
    if (config.algorithm === 'SM4') {
      return encryptBySM4(word, config);
    } else {
      throw new Error('不支持此加密算法：' + config.algorithm);
    }
  } else {
    return word;
  }
};

// 解密
export const decrypt = word => {
  if (config.enabled) {
    if (config.algorithm === 'SM4') {
      return decryptBySM4(word, config);
    } else {
      throw new Error('不支持此加密算法：' + config.algorithm);
    }
  } else {
    return word;
  }
};

const convertPadding = padding => {
  if (padding === 'NoPadding') {
    return 'none';
  } else if (padding === 'PKCS5Padding') {
    return 'pkcs#5';
  } else {
    return 'none';
  }
};

export const encryptBySM4 = (word, config) => {
  if (!word) return '';
  return sm4.encrypt(word, strToHex(config?.key), {
    padding: convertPadding(config?.padding),
    mode: config?.mode?.toLowerCase(),
    iv: strToHex(config?.iv),
  });
};

export const decryptBySM4 = (word, config) => {
  if (!word) return '';
  return sm4.decrypt(word, strToHex(config?.key), {
    padding: convertPadding(config?.padding),
    mode: config?.mode?.toLowerCase(),
    iv: strToHex(config?.iv),
  });
};

/**
 * 解密数据
 * @param {Object} dataHex 加密的密文数据
 */

export function decryptDataBySM2(encryptData) {
  if (encryptData && typeof encryptData === 'string' && encryptData.constructor === String) {
    const decryptData = sm2.doDecrypt(encryptData.substring(2), privateKey, 1);
    return decryptData;
  }
}

export function encryptDataBySM2(str) {
  const publicKey = process.env.VUE_APP_PUBK;
  if (str && typeof str === 'string' && str.constructor === String) {
    const encryptData = sm2.doEncrypt(str, publicKey, 1);
    return '04'.concat(encryptData);
  }
}

export function myDecryptBySM2(encryptData) {
  // const privateKeySm2 = process.env.VUE_APP_PRIK_SM2;
  const privateKeySm2 = '4ac2b4f0e7f0a47d8067c752fa27b0ca68eb6f61104dfef8371fa6cedf447efb';
  if (encryptData && typeof encryptData === 'string' && encryptData.constructor === String) {
    const decryptData = sm2.doDecrypt(encryptData.substring(2), privateKeySm2, 1);
    return decryptData;
  }
}

export function myEncryptBySM2(str) {
  // const publicKeySm2 = process.env.VUE_APP_PUBK_SM2;
  const publicKeySm2 =
    '0413ae8e08b60935f6453daa65fbecb51e24beff7643de01ad49b0208e6c9fd104b234eec574d47aaf4508ccf10bc3194a887ba555e581e17c44c0dd236e1a7a4d';
  if (str && typeof str === 'string' && str.constructor === String) {
    const encryptData = sm2.doEncrypt(str, publicKeySm2, 1);
    return '04'.concat(encryptData);
  }
}

// 简单的XOR对称加密，密文长度与明文相同
const XOR_KEY = 'my-secret-key-1234567890';

/**
 * URL安全的Base64编码（将+替换为-，/替换为_，去掉=）
 * @param {string} base64Str 标准Base64字符串
 * @returns {string} URL安全的Base64字符串
 */
function urlSafeBase64Encode(base64Str) {
  return base64Str.replace(/\+/g, '-').replace(/\//g, '_').replace(/=/g, '');
}

/**
 * URL安全的Base64解码（将-替换为+，_替换为/，补上=）
 * @param {string} safeStr URL安全的Base64字符串
 * @returns {string} 标准Base64字符串
 */
function urlSafeBase64Decode(safeStr) {
  // 补上可能缺失的=号
  let base64Str = safeStr.replace(/-/g, '+').replace(/_/g, '/');
  const padLength = 4 - (base64Str.length % 4);
  if (padLength !== 4) {
    base64Str += '='.repeat(padLength);
  }
  return base64Str;
}

/**
 * XOR对称加密
 * @param {string} plaintext 明文
 * @returns {string} 返回URL安全的Base64编码的密文
 */
export function xorEncrypt(plaintext) {
  if (!plaintext) return '';

  let result = '';
  const keyLength = XOR_KEY.length;

  for (let i = 0; i < plaintext.length; i++) {
    const charCode = plaintext.charCodeAt(i) ^ XOR_KEY.charCodeAt(i % keyLength);
    result += String.fromCharCode(charCode);
  }

  // 使用Base64编码后转换为URL安全格式
  const base64Result = btoa(result);
  return urlSafeBase64Encode(base64Result);
}

/**
 * XOR对称解密
 * @param {string} ciphertext URL安全的Base64编码的密文
 * @returns {string} 解密后的明文
 */
export function xorDecrypt(ciphertext) {
  if (!ciphertext) return '';

  try {
    // 先转换为标准Base64格式，再进行解码
    const standardBase64 = urlSafeBase64Decode(ciphertext);
    const decoded = atob(standardBase64);
    let result = '';
    const keyLength = XOR_KEY.length;

    for (let i = 0; i < decoded.length; i++) {
      const charCode = decoded.charCodeAt(i) ^ XOR_KEY.charCodeAt(i % keyLength);
      result += String.fromCharCode(charCode);
    }

    return result;
  } catch (error) {
    console.error('解密失败:', error);
    return '';
  }
}
